// app/shared/components/ProtectedViewComponent.tsx
// app/shared/components/protected/ProtectedViewComponent.tsx
"use client";

import { ReactNode } from "react";

// ### CODE CHANGES START ###
// We ONLY import useAuthHook now. useSubscriptionHook is gone.
import { useAuthHook } from "@/app/auth/services_and_hooks/useAuthHook";
import { SiteFooter } from "@/components/site-footer";
import { SiteHeader } from "@/components/site-header";
import { Skeleton } from "@/components/ui/skeleton";
// ### CODE CHANGES FINISH ###

type ProtectedViewProps = {
  children: ReactNode;
  fallback?: ReactNode;
  loginPrompt?: ReactNode;
  subscriptionPrompt?: ReactNode;
  requiredSubscription?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
  hideGetStarted?: boolean; // Pass through to SiteHeader
};

export function ProtectedViewComponent({
  children,
  fallback = <Skeleton className="w-full h-32" />,
  loginPrompt = (
    <div className="p-4 border rounded">
      <p>
        Please{" "}
        <a href="/login" className="underline">
          log in
        </a>{" "}
        to view this content.
      </p>
    </div>
  ),
  subscriptionPrompt = (
    <div className="p-4 border rounded">
      <p>This content requires an active subscription.</p>
      <a href="/pricing" className="button mt-2 underline">
        View Plans
      </a>
    </div>
  ),
  requiredSubscription = false,
  showHeader = true,
  showFooter = true,
  hideGetStarted = false,
}: ProtectedViewProps) {
  // All state now comes from a single, unified hook.
  const { isLoading, isAuthenticated, hasActiveSubscription } = useAuthHook();

  const showLoading = isLoading;
  const showLoginPrompt = !isLoading && !isAuthenticated;
  const showSubscriptionPrompt =
    !isLoading &&
    isAuthenticated &&
    requiredSubscription &&
    !hasActiveSubscription;

  const renderContent = () => {
    if (showLoading) return fallback;
    if (showLoginPrompt) return loginPrompt;
    if (showSubscriptionPrompt) return subscriptionPrompt;
    return children; // All checks passed
  };

  const rendered = (
    <>
      {showHeader && <SiteHeader hideGetStarted={hideGetStarted} />}
      <main className={showHeader || showFooter ? "flex-1" : undefined}>
        {renderContent()}
      </main>
      {showFooter && <SiteFooter />}
    </>
  );

  if (showHeader || showFooter) {
    return <div className="flex flex-col min-h-screen">{rendered}</div>;
  }
  return rendered;
}

// v1
// // app/shared/components/protected/ProtectedViewComponent.tsx
// "use client";

// import { ReactNode } from "react";

// // ### CODE CHANGES START ###
// // We ONLY import useAuthHook now. useSubscriptionHook is gone.
// import { useAuthHook } from "@/app/auth/services_and_hooks/useAuthHook";
// import { SiteFooter } from "@/components/site-footer";
// import { SiteHeader } from "@/components/site-header";
// import { Skeleton } from "@/components/ui/skeleton";
// // ### CODE CHANGES FINISH ###

// type ProtectedViewProps = {
//   children: ReactNode;
//   fallback?: ReactNode;
//   loginPrompt?: ReactNode;
//   subscriptionPrompt?: ReactNode;
//   requiredSubscription?: boolean;
//   showHeader?: boolean;
//   showFooter?: boolean;
//   hideGetStarted?: boolean; // Pass through to SiteHeader
// };

// export function ProtectedViewComponent({
//   children,
//   fallback = <Skeleton className="w-full h-32" />,
//   loginPrompt = <div className="p-4 border rounded"><p>Please <a href="/login" className="underline">log in</a> to view this content.</p></div>,
//   subscriptionPrompt = (
//     <div className="p-4 border rounded">
//       <p>This content requires an active subscription.</p>
//       <a href="/pricing" className="button mt-2 underline">View Plans</a>
//     </div>
//   ),
//   requiredSubscription = false,
//   showHeader = true,
//   showFooter = true,
//   hideGetStarted = false,
// }: ProtectedViewProps) {
//   // ### CODE CHANGES START ###
//   // Destructure EVERYTHING from the single useAuthHook.
//   // isLoading now represents the combined loading state for auth and subscription.
//   const {
//     user,
//     isLoading,
//     hasActiveSubscription,
//     isAuthenticated,
//   } = useAuthHook();
//   // ### CODE CHANGES FINISH ###

//   if (isLoading) {
//     // Show loading with header/footer if requested
//     if (showHeader || showFooter) {
//       return (
//         <div className="min-h-screen bg-gray-50">
//           {showHeader && <SiteHeader hideGetStarted={hideGetStarted} />}
//           <main className="flex-1 flex items-center justify-center py-20">
//             {fallback}
//           </main>
//           {showFooter && <SiteFooter />}
//         </div>
//       );
//     }
//     return <>{fallback}</>;
//   }

//   // Not authenticated
//   if (!isAuthenticated || !user) {
//     if (showHeader || showFooter) {
//       return (
//         <div className="min-h-screen bg-gray-50">
//           {showHeader && <SiteHeader hideGetStarted={hideGetStarted} />}
//           <main className="flex-1 flex items-center justify-center py-20">
//             {loginPrompt}
//           </main>
//           {showFooter && <SiteFooter />}
//         </div>
//       );
//     }
//     return <>{loginPrompt}</>;
//   }

//   // Subscription required but user doesn't have one
//   if (requiredSubscription && !hasActiveSubscription) {
//     if (showHeader || showFooter) {
//       return (
//         <div className="min-h-screen bg-gray-50">
//           {showHeader && <SiteHeader hideGetStarted={hideGetStarted} />}
//           <main className="flex-1 flex items-center justify-center py-20">
//             {subscriptionPrompt}
//           </main>
//           {showFooter && <SiteFooter />}
//         </div>
//       );
//     }
//     return <>{subscriptionPrompt}</>;
//   }

//   // If all checks pass, render the protected content with optional header/footer
//   if (showHeader || showFooter) {
//     return (
//       <div className="min-h-screen bg-gray-50">
//         {showHeader && <SiteHeader hideGetStarted={hideGetStarted} />}
//         <main className="flex-1">
//           {children}
//         </main>
//         {showFooter && <SiteFooter />}
//       </div>
//     );
//   }

//   // No header/footer requested, just return the children
//   return <>{children}</>;
// }

// v1
// "use client";

// import { ReactNode } from "react";

// import { useAuthHook } from "@/app/auth/services_and_hooks/useAuthHook"; // Assuming this path
// import { useSubscriptionHook } from "@/app/auth/services_and_hooks/useSubscriptionHook"; // Adjust path
// import { Skeleton } from "@/components/ui/skeleton"; // Assuming shadcn/ui skeleton

// type ProtectedViewProps = {
//   children: ReactNode;
//   fallback?: ReactNode; // Fallback while loading
//   loginPrompt?: ReactNode; // Fallback if not logged in
//   subscriptionPrompt?: ReactNode; // Fallback if subscription required and missing
//   requiredSubscription?: boolean;
// };

// export function ProtectedViewComponent({
//   children,
//   fallback = <Skeleton className="w-full h-32" />,
//   loginPrompt = <div className="p-4 border rounded"><p>Please <a href="/login" className="underline">log in</a> to view this content.</p></div>, // Updated href
//   subscriptionPrompt = (
//     <div className="p-4 border rounded">
//       <p>This content requires an active subscription.</p>
//       <a href="/pricing" className="button mt-2 underline">View Plans</a> {/* Example link */}
//     </div>
//   ),
//   requiredSubscription = false,
// }: ProtectedViewProps) {
//   const { user, isLoading: isLoadingAuth } = useAuthHook(); // Removed error destructuring
//   const {
//     // subscription, // subscription object itself might not be directly needed here
//     isLoadingSubscription,
//     hasActiveSubscription,
//     subscriptionError // Added subscriptionError
//   } = useSubscriptionHook();

//   // Combined loading state
//   if (isLoadingAuth || (requiredSubscription && isLoadingSubscription)) {
//     return <>{fallback}</>; // Ensure fallback is ReactNode
//   }

//   // Not authenticated
//   if (!user) {
//     return <>{loginPrompt}</>; // Ensure loginPrompt is ReactNode
//   }

//   // Handle subscription fetch errors if subscription is required
//   if (requiredSubscription && subscriptionError) {
//     console.error("Subscription fetch error in ProtectedViewComponent:", subscriptionError);
//     return <div className="p-4 border rounded text-red-600"><p>Could not verify subscription status. Please try refreshing the page.</p></div>;
//   }

//   // Subscription required but user doesn't have one (and no error fetching subscription)
//   if (requiredSubscription && !hasActiveSubscription) {
//     return <>{subscriptionPrompt}</>; // Ensure subscriptionPrompt is ReactNode
//   }

//   // User is authenticated and meets subscription requirements (if any)
//   return <>{children}</>;
// }
